/**
 * Modal — full-screen overlay dialog with Framer-Motion spring enter/exit,
 * focus trap, Esc-to-close, and a portal to `document.body`.
 * Reduced-motion: backdrop + panel snap in instantly with no spring.
 */
import {
  useEffect,
  useRef,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import clsx from 'clsx';
import { springTransition, instantTransition, useMotionPreference } from '../../lib/motion';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  /** Controls the maximum width of the panel. Defaults to 'md'. */
  size?: 'sm' | 'md' | 'lg';
}

const SIZE_CLASSES: Record<NonNullable<ModalProps['size']>, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-2xl',
};

/** All focusable elements inside a container — used for tab-trap. */
function getFocusable(el: HTMLElement): HTMLElement[] {
  return Array.from(
    el.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])',
    ),
  );
}

export function Modal({ open, onClose, title, children, size = 'md' }: ModalProps) {
  const pref = useMotionPreference();
  const panelRef = useRef<HTMLDivElement>(null);
  const transition = pref === 'full' ? springTransition : instantTransition;

  // Focus trap: on open, focus the panel; cycle Tab/Shift+Tab within it.
  useEffect(() => {
    if (!open) return;
    const panel = panelRef.current;
    if (!panel) return;
    const focusable = getFocusable(panel);
    focusable[0]?.focus();

    function handleTab(e: globalThis.KeyboardEvent) {
      if (e.key !== 'Tab') return;
      const els = getFocusable(panel!);
      if (els.length === 0) { e.preventDefault(); return; }
      const first = els[0]!;
      const last = els[els.length - 1]!;
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    }
    document.addEventListener('keydown', handleTab);
    return () => document.removeEventListener('keydown', handleTab);
  }, [open]);

  // Esc to close.
  function handleKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Escape') onClose();
  }

  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  };

  const panelVariants = {
    hidden: { opacity: 0, scale: 0.94, y: 12 },
    visible: { opacity: 1, scale: 1, y: 0 },
  };

  const modal = (
    <AnimatePresence>
      {open && (
        <motion.div
          key="modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby={title ? 'modal-title' : undefined}
          variants={backdropVariants}
          initial="hidden"
          animate="visible"
          exit="hidden"
          transition={pref === 'full' ? { type: 'tween', duration: 0.2 } : instantTransition}
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(2, 6, 13, 0.75)', backdropFilter: 'blur(6px)' }}
          onKeyDown={handleKeyDown}
          onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
          <motion.div
            ref={panelRef}
            key="modal-panel"
            variants={panelVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            transition={transition}
            tabIndex={-1}
            className={clsx(
              'relative w-full',
              SIZE_CLASSES[size],
              'rounded-2xl',
              'bg-bg-elevated',
              'ring-1 ring-surface-stroke',
              'shadow-glass',
              'focus:outline-none',
            )}
          >
            {/* Header */}
            {title !== undefined && (
              <div className="flex items-center justify-between border-b border-surface-stroke px-5 py-4">
                <h2 id="modal-title" className="text-sm font-semibold text-text-1">
                  {title}
                </h2>
                <button
                  type="button"
                  aria-label="Close"
                  onClick={onClose}
                  className="rounded p-1 text-text-3 transition-colors hover:bg-surface-glass hover:text-text-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round">
                    <line x1="2" y1="2" x2="12" y2="12" />
                    <line x1="12" y1="2" x2="2" y2="12" />
                  </svg>
                </button>
              </div>
            )}

            {/* Body */}
            <div className="px-5 py-4">{children}</div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return createPortal(modal, document.body);
}
